"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDishDetails = void 0;
var react_1 = require("react");
var graphql_api_patient_1 = require("@fdha/graphql-api-patient");
var MealDeliveryProvider_1 = require("./MealDeliveryProvider");
// Platform-agnostic dish details hook
var useDishDetails = function () {
    var _a, _b;
    var _c = (0, MealDeliveryProvider_1.useMealDeliveryContext)(), actions = _c.actions, bridge = _c.bridge;
    var _d = (0, MealDeliveryProvider_1.useSelections)(), entrees = _d.entrees, snacksAndSides = _d.snacksAndSides;
    var counts = (0, MealDeliveryProvider_1.useMealDeliveryCounts)();
    // const MealOptionType = bridge.MealOptionType;
    // Create initial state with useMemo to prevent recreation on every render
    var initialState = (0, react_1.useMemo)(function () { return ({
        visible: false,
        selectedDish: {
            dishId: '',
            mealOptionType: graphql_api_patient_1.MealOptionType.Dish,
            sectionName: 'entreeDishes',
            variantId: '',
            menuOptionId: '',
            quantity: 0,
        },
    }); }, []);
    var _e = (0, react_1.useState)(initialState), state = _e[0], setState = _e[1];
    var _f = (0, react_1.useState)(0), quantity = _f[0], setQuantity = _f[1];
    var _g = (0, react_1.useState)(''), selectedVariant = _g[0], setSelectedVariant = _g[1];
    var _h = (0, react_1.useState)(false), variantError = _h[0], setVariantError = _h[1];
    var isMealKit = state.selectedDish.mealOptionType === graphql_api_patient_1.MealOptionType.MealKit;
    // Fetch data based on mealOptionType using platform-specific hooks
    var _j = ((_a = bridge.useGetDishQuery) === null || _a === void 0 ? void 0 : _a.call(bridge, {
        variables: { id: state.selectedDish.dishId },
        skip: !state.visible || isMealKit,
    })) || { data: undefined, loading: false }, dishData = _j.data, dishLoading = _j.loading;
    var _k = ((_b = bridge.useGetMealKitQuery) === null || _b === void 0 ? void 0 : _b.call(bridge, {
        variables: { id: state.selectedDish.dishId },
        skip: !state.visible || !isMealKit,
        fetchPolicy: 'network-only',
    })) || { data: undefined, loading: false }, mealKitData = _k.data, mealKitLoading = _k.loading;
    var isLoading = dishLoading || mealKitLoading;
    // Handle variant selection: initial from state or auto-select if only one option
    (0, react_1.useEffect)(function () {
        var _a, _b;
        // First priority: use variant from initial state/props
        if (state.selectedDish.variantId) {
            setSelectedVariant(state.selectedDish.variantId);
            return;
        }
        // Second priority: auto-select if meal kit has only one variant
        if (isMealKit && ((_b = (_a = mealKitData === null || mealKitData === void 0 ? void 0 : mealKitData.mealKit) === null || _a === void 0 ? void 0 : _a.variants) === null || _b === void 0 ? void 0 : _b.length) === 1) {
            var singleVariant = mealKitData.mealKit.variants[0];
            if (singleVariant.variantId) {
                setSelectedVariant(singleVariant.variantId.toString());
                return;
            }
        }
        // Reset variant if no initial variant and it's not a meal kit
        if (!state.selectedDish.variantId && !isMealKit) {
            setSelectedVariant('');
        }
    }, [
        isMealKit,
        mealKitData,
        state.visible,
        state.selectedDish.dishId,
        state.selectedDish.variantId,
    ]);
    // Reset quantity when modal is closed
    (0, react_1.useEffect)(function () {
        if (!state.visible) {
            setQuantity(0);
        }
        setVariantError(false);
    }, [state.visible]);
    var setDishDetails = (0, react_1.useCallback)(function (details) {
        setState(__assign({}, details));
    }, []);
    var resetDishDetails = (0, react_1.useCallback)(function () {
        setState(initialState);
    }, [initialState]);
    var onVariantChange = (0, react_1.useCallback)(function (variant) {
        setSelectedVariant(variant);
        setVariantError(false);
    }, []);
    var handleAdd = (0, react_1.useCallback)(function (isMealKit) {
        var menu = state.selectedDish.sectionName === 'entreeDishes'
            ? entrees
            : snacksAndSides;
        var dishToUpdate = menu.find(function (s) {
            return s.dishId === state.selectedDish.dishId &&
                s.menuOptionId === state.selectedDish.menuOptionId &&
                (isMealKit ? s.mealkitVariantId === selectedVariant : true);
        });
        if (state.selectedDish.menuOptionId) {
            actions.updateSelection({
                isEntree: state.selectedDish.sectionName === 'entreeDishes',
                menuOptionId: state.selectedDish.menuOptionId,
                quantity: ((dishToUpdate === null || dishToUpdate === void 0 ? void 0 : dishToUpdate.quantity) || 0) + quantity,
                mealkitVariantId: selectedVariant,
            });
        }
        resetDishDetails();
    }, [
        actions,
        entrees,
        snacksAndSides,
        state.selectedDish.dishId,
        state.selectedDish.menuOptionId,
        state.selectedDish.sectionName,
        quantity,
        selectedVariant,
        resetDishDetails,
    ]);
    var validateAndAdd = (0, react_1.useCallback)(function () {
        if (state.selectedDish.mealOptionType === graphql_api_patient_1.MealOptionType.Dish) {
            handleAdd(false);
            return true;
        }
        if (!selectedVariant) {
            setVariantError(true);
            return false;
        }
        handleAdd(true);
        return true;
    }, [state.selectedDish.mealOptionType, handleAdd, selectedVariant]);
    return {
        // State
        visible: state.visible,
        selectedDish: state.selectedDish,
        quantity: quantity,
        selectedVariant: selectedVariant,
        variantError: variantError,
        isLoading: isLoading,
        // Data
        dishData: dishData,
        mealKitData: mealKitData,
        // Required quantities from context
        requiredQuantity: state.selectedDish.sectionName === 'entreeDishes'
            ? counts.maxEntrees
            : counts.maxSnacksAndSides,
        selectedQuantity: state.selectedDish.sectionName === 'entreeDishes'
            ? counts.selectedEntreesCount
            : counts.selectedSnacksAndSidesCount,
        // Actions
        setDishDetails: setDishDetails,
        resetDishDetails: resetDishDetails,
        handleAdd: handleAdd,
        setQuantity: setQuantity,
        onVariantChange: onVariantChange,
        validateAndAdd: validateAndAdd,
    };
};
exports.useDishDetails = useDishDetails;
